import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';

import { getPublicFirestoreUrl } from '../../modules/encoding-helpers.js';
import { q1ProjectCardStyles } from './q1-project-card-styles.js';

export class Q1ProjectCard extends LitElement {
  static styles = [ q1ProjectCardStyles ];

  static properties = {    

    /** The project object to display. Must be enriched with the enrichProject() helper. */
    project: { type: Object },

    /** Provide this if there's a chance the project will be empty. Used in empty state. */
    projectKey: { type: String, attribute: 'project-key' },

    /** Set this to suppress the link on the project card. */
    noLink: { type: Boolean, attribute: 'no-link' },

    /** Set this in order to force the focus/hover state to always be active. */
    alwaysActive: { type: Boolean, attribute: 'always-active' },

  };

  constructor() {
    super();

    this.project = null;
    this.projectKey = null;

    this.noLink = false;
    this.alwaysActive = false;
  }

  updated(changedProperties) {
    if (changedProperties.has('project')) {
      if (!this.project) this.noLink = true;

      if (this.project?.colorPrimary) {
        this.style.setProperty('--theme-color-primary', this.project.colorPrimary);
        this.style.setProperty('--theme-color-secondary', this.project.colorSecondary);
        this.style.setProperty('--theme-text-color-primary', this.project.textColorPrimary_hex);
        this.style.setProperty('--theme-text-color-secondary', this.project.textColorSecondary_hex);
      } else {
        this.style.setProperty('--theme-color-primary', 'var(--teal-7)');
        this.style.setProperty('--theme-color-secondary', 'var(--teal-1)');
        this.style.setProperty('--theme-text-color-primary', 'var(--teal-8)');
        this.style.setProperty('--theme-text-color-secondary', 'var(--teal-8)');
      }
    }
  }

  handleLinkClick(event) {
    if (this.noLink) event.preventDefault();
  }

  get isEmpty() {
    return !this.project?.id;
  }

  get displayName() {
    return this.project?.name || 'q1 Network Project';
  }

  get avatarImage() {
    if (
      (typeof this.project?.avatar === 'string')
      && this.project.avatar.startsWith('http')
    ) return this.project.avatar;

    return getPublicFirestoreUrl(this.project?.avatar?.small?.path);
  }

  get memberCount() {
    return this.project?.memberCount || 1;
  }

  get followerCount() {
    return this.project?.followerCount || 1;
  }

  get projectEmptySubtitle() {
    if (this.projectKey) return `Project Key: ${this.projectKey}`;
    return 'No project data available';
  }

  render() {
    return html`
      <a
        id="project-card"
        part="card"
        href="/projects/${this.project?.key}"
        @click=${this.handleLinkClick}
        class=${classMap({ 'always-active': this.alwaysActive })}
      >
        <sl-avatar
          part="avatar"
          id="project-avatar"
          shape="rounded"
          label="${this.project?.name}"
          image=${this.avatarImage}
        >
          <sl-icon slot="icon" library="material" name="business_center"></sl-icon>
        </sl-avatar>

        <div part="info" class="project-card-info">
          <div class="project-title">${this.displayName}</div>
          ${this.isEmpty ? html`
            <div class="project-empty">${this.projectEmptySubtitle}</div>
          ` : html`
            <ul>
              <li>
                <sl-icon library="material" name="groups"></sl-icon>
                ${this.memberCount || 1}
                ${this.memberCount > 1 ? 'members' : 'member'}
              </li>
              <li>
                <sl-icon library="material" name="bigtop_updates"></sl-icon>
                ${this.followerCount || 1} 
                ${this.followerCount > 1 ? 'followers' : 'follower'} 
              </li>
            </ul>
          `}
        </div>
      </a>
    `;
  }

}

customElements.define('q1-project-card', Q1ProjectCard);
